import { Injectable, OnDestroy } from '@angular/core';
import { ofType } from '@ngrx/effects';
import { ActionsSubject } from '@ngrx/store';
import {
  EventService,
  StateEventService,
  User,
  createFrom
} from '@spartacus/core';
import { Order } from '@spartacus/order/root';
import { UserAccountFacade } from '@spartacus/user/account/root';
import { Observable, Subscription } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { CustomCheckoutActions } from '../../../../../custom/core/src/checkout/store/actions';
import { EcommerceProductItem } from '../../cart/CartEvents.model';
import { TagManagerFeatureService } from '../../tag-manager-feature.service';
import { CustomOrderPlacedEvent } from './custom-custom-checkout.events';
import { CustomCleanEcommerceEvent } from "../../common/custom-common.event";
import { PURCHASE_CONFIRMATION_SUCCESS } from "../../../../../custom/core/src/checkout/store/actions/checkout.action";

@Injectable({
  providedIn: 'root',
})
export class CustomCheckoutEventBuilder implements OnDestroy {
  protected subscriptions: Subscription = new Subscription();
  user$: Observable<User | undefined> = this.userAccount.get();
  professional = false;

  constructor(
    protected actions: ActionsSubject,
    protected stateEventService: StateEventService,
    protected eventService: EventService,
    protected tagManagerFeatureService: TagManagerFeatureService,
    private userAccount: UserAccountFacade,
  ) {
    this.register();

    this.subscriptions.add(this.user$.subscribe(user => {
      this.professional = user?.professional || false;
    }));
  }

  /**
   * Registers checkout events
   */
  protected register(): void {
    this.orderPlacedEvent();
    this.eventService.register(CustomOrderPlacedEvent, this.buildPurchaseEvent());
  }


  /**
   * Builds the purchase events
   */
  protected buildPurchaseEvent(): Observable<CustomOrderPlacedEvent> {
    return this.actions.pipe(
      ofType(PURCHASE_CONFIRMATION_SUCCESS),
      tap(() => this.eventService.dispatch(<CustomCleanEcommerceEvent>{ecommerce: null}, CustomCleanEcommerceEvent)),
      map((action: any) => {
          const order = action.payload.order;
          const productList: Array<EcommerceProductItem> = this.tagManagerFeatureService.getPurchasedProductItems(order);
          return createFrom(CustomOrderPlacedEvent, {
            event: 'purchase',
            ecommerce: {
              transaction_id: order.code,
              value: this.getValue(order),
              tax: this.getTaxes(order),
              subtotalTax: this.getSubtotalTax(order),
              shipping: this.tagManagerFeatureService.toPriceFormatted(order.deliveryCost.value),
              currency: order.totalPrice.currencyIso,
              coupon: this.getCoupon(order),
              shipping_info: order.deliveryMode?.description || '',
              payment_info: order.paymentMode?.name || '',
              items: productList,
            }
          } as CustomOrderPlacedEvent);
        },
      ),
    );
  }

  protected getValue(order: Order): number {
    const value = this.professional ?
      (order.subTotal.value - order.orderDiscounts.value) :
      (order.subTotal.value - order.orderDiscounts.value) - this.getSubtotalTax(order);
    return this.tagManagerFeatureService.toPriceFormatted(value);
  }

  protected getSubtotalTax(order: Order): number {
    const value = this.professional ?
      ((order.subTotal.value - order.orderDiscounts.value) * 1.21) :
      ((order.subTotal.value - order.orderDiscounts.value) - ((order.subTotal.value - order.orderDiscounts.value) / 1.21));
    return this.tagManagerFeatureService.toPriceFormatted(value);
  }

  protected getTaxes(order: Order): number {
    const value = this.professional ?
      (order.totalTax.value) :
      ((order.totalPriceWithTax.value) - (order.totalPriceWithTax.value / 1.21))
    return this.tagManagerFeatureService.toPriceFormatted(value);
  }

  protected getCoupon(order: Order): string {
    let coupons = '';
    if (!!order?.appliedVouchers && order?.appliedVouchers.length > 0) {
      order?.appliedVouchers?.map((voucher, i) => {
        coupons += voucher.voucherCode;
        if (i < (order.appliedVouchers.length - 1)) {
          coupons += ',';
        }
      });
    }
    return coupons;
  }

  /**
   * Register an order successfully placed event
   */
  protected orderPlacedEvent(): void {
    this.stateEventService.register({
      action: CustomCheckoutActions.PLACE_ORDER_SUCCESS,
      event: CustomOrderPlacedEvent,
    });
  }

  ngOnDestroy(): void {
    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }
}
